import List from "components/list";
import { useParams } from "react-router-dom";
import { usePermissions } from "contexts/permission";
import { StatusCell } from "@/components/status/cell";
import { Button } from "ui/index";
import { Icon } from "components/index";
import { EDIT } from "icons/src";
import EmployeeInput from "components/employee/input";
import { useMutation } from "@tanstack/react-query";
import { updateItemInCollection } from "directus/src/operations";
import { invalidateAllQueries } from "utils/utils";
import { eventBus } from "utils/eventBus";
import { toast } from "sonner";
import { UserFieldInput } from "permissions/components/user-field-input";
import { ROUTE } from "@/lib/utils";

export default function MilestoneCoordinatorList() {
  const { employeeId } = useParams();
  const permissions = usePermissions();
  const canEditCoordinator = permissions.hasFieldAccess("milestones", "update", "coordinator");
  const userFilter = employeeId ? { _eq: employeeId } : { user: { _eq: "$CURRENT_USER" } };

  const storageKey = employeeId ? `profile-mc-milestones-${employeeId}` : "profile-mc-milestones";

  const updateMilestoneMutation = useMutation({
    mutationFn: async ({ id, coordinatorId }) => {
      await updateItemInCollection("milestones", id, { coordinator: coordinatorId });
    },
    onSuccess: () => {
      invalidateAllQueries();
      toast.success("Milestone Coordinator Updated Successfully");
      eventBus.emit("refresh:list", `milestones${storageKey}`);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const fields = [
    "id",
    "name",
    "status",
    "job.id",
    "job.name",
    "job.project.name",
    "coordinator.id",
    "coordinator.first_name",
    "coordinator.last_name",
    "coordinator.avatar",
  ];

  const columns = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      cell: StatusCell,
    },
    {
      id: "project",
      header: "Project",
      accessorKey: "job.project.name"
    },
    {
      id: "job",
      header: "Job",
      accessorKey: "job.name"
    },

    {
      id: "coordinator",
      header: "MC",
      hidden: !canEditCoordinator,
      href: false,
      cell: ({ row }) => {
        const milestone = row.original;
        return (
          <UserFieldInput
            targetItem={milestone.id}
            field="coordinator"
            collection="milestones"
            showWhenDisabled={false}
          >
            <EmployeeInput
              set="milestones_coordinating"
              value={milestone.coordinator}
              singleSelect={true}
              footer={({ setOpen, selectedEmployees }) => (
                <Button
                  onClick={async () => {
                    const selectedEmployee = selectedEmployees?.[0];
                    if (selectedEmployee) {
                      await updateMilestoneMutation.mutateAsync({
                        id: milestone.id,
                        coordinatorId: selectedEmployee.id,
                      });
                    }
                    setOpen(false);
                  }}
                >
                  Assign
                </Button>
              )}
            >
              <Button variant="ghost">
                <Icon icon={EDIT} />
              </Button>
            </EmployeeInput>
          </UserFieldInput>
        );
      }
    }
  ];

  const filters = {
    coordinator: userFilter,
    status: { _nin: ["archived", "closed", "cancelled"] },
  };

  return (
    <List
      title="Milestones"
      storageKey={storageKey}
      endPoint="milestones"
      filters={filters}
      columns={columns.filter(col => !col.hidden)}
      meta={{ fields }}
      sortBy="date_created"
      href={(item) => ROUTE.MILESTONE(item.id)}
    />
  );
}
